import { ReactNode } from 'react';
import { Box, Typography, useTheme, Stack, Tooltip } from '@mui/material';
import { AiOutlineInfoCircle } from 'react-icons/ai';

interface BusinessInsightStatCardProps {
  title: string;
  count: number;
  subtitle?: string;
  icon: ReactNode;
  tooltip: string;
}

export default function BusinessInsightStatCard({
  title,
  count,
  subtitle,
  icon,
  tooltip
}: BusinessInsightStatCardProps) {
  const theme = useTheme();

  return (
    <Box
      bgcolor='#fff'
      border='1px solid #ECECEC'
      paddingY={1.5}
      paddingX={2}
      borderRadius={2}
      borderLeft={`3px solid ${theme.palette.primary.main}`}
      height='100%'
    >
      <Box display='flex' gap='4px' alignItems='center' justifyContent='space-between'>
        <Stack direction='row' alignItems='center' gap={0.5} flex={1} minWidth={0}>
          <Typography color='secondary' fontWeight={500} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            {title}
          </Typography>
          <Tooltip
            title={tooltip}
            arrow
            placement='top'
            slotProps={{
              tooltip: {
                sx: {
                  bgcolor: 'black',
                  color: 'white',
                  boxShadow: 3,
                  fontSize: '0.85rem',
                  borderRadius: 1,
                  p: 1.25,
                  lineHeight: 1.4,
                  maxWidth: 320
                }
              },
              arrow: {
                sx: { color: 'black' }
              }
            }}
          >
            <Box
              component='span'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                color: 'text.secondary',
                cursor: 'help',
                flexShrink: 0,
                '& svg': { width: 16, height: 16 }
              }}
            >
              <AiOutlineInfoCircle />
            </Box>
          </Tooltip>
        </Stack>
        <Box
          bgcolor={theme.palette.primary.light}
          color={theme.palette.primary.main}
          borderRadius='50%'
          height={{ xs: '28px', sm: '32px' }}
          width={{ xs: '28px', sm: '32px' }}
          display='flex'
          alignItems='center'
          justifyContent='center'
          flexShrink={0}
        >
          {icon}
        </Box>
      </Box>
      <Stack direction='row' alignItems='baseline' spacing={1}>
        <Typography variant='h5' mt={1} fontWeight={500} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {count ?? 0}
        </Typography>
        {subtitle && <Typography fontSize={12}>{subtitle}</Typography>}
      </Stack>
    </Box>
  );
}
